import { useState } from 'react'
import { usePermissions } from '@/hooks/usePermissions'
import { PERMISSIONS } from '@/lib/permissions'
import { CalendarCheck } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { DisabledHint } from '@/components/ui/disabled-hint'
import { ApiError } from '@/lib/api/client'
import { initializeProjectPlanning } from '@/lib/api/v2/planning'
import { showErrorToast, showSuccessToast } from '@/lib/toast'

interface PlanningActivationCardProps {
  projectId: string
  /** Refetches executionDetails — flips the block to the order picker without a page reload. */
  onActivated: () => Promise<void> | void
}

/**
 * SOLY-PLAN-001 MS-34 — empty state for projects sold BEFORE Features:Planning was enabled:
 * one click provisions the phase-1 métré + pose server-side (existing execution-details fields
 * are preserved) and the project appears in the planning cockpit.
 */
export function PlanningActivationCard({ projectId, onActivated }: PlanningActivationCardProps) {
  const { can, reasonIfDenied } = usePermissions()
  const [isActivating, setIsActivating] = useState(false)

  const handleActivate = async () => {
    if (isActivating) return
    setIsActivating(true)
    try {
      await initializeProjectPlanning(projectId)
      showSuccessToast('Suivi de chantier activé')
      // On success the card is replaced by the picker (refetch below) — don't re-enable.
      await onActivated()
    } catch (e) {
      const code = e instanceof ApiError ? e.code : undefined
      if (code === 'NO_ACCEPTED_ORDER') {
        showErrorToast("Acceptez d'abord un devis ou confirmez la vente")
      } else if (code === 'PROJECT_INACTIVE') {
        showErrorToast('Ce projet est archivé — le suivi de chantier ne peut pas être activé')
      } else {
        showErrorToast("L'activation du suivi de chantier a échoué, réessayez")
      }
      setIsActivating(false)
    }
  }

  return (
    <section id="planning-activation" className="space-y-4" data-testid="project-planning-activation">
      <div>
        <h2 className="text-lg font-semibold">Suivi de chantier</h2>
        <p className="text-sm text-muted-foreground">Métré, pose et commandes fournisseurs</p>
      </div>
      <div className="rounded-lg border bg-card p-6 text-sm text-muted-foreground">
        <div className="space-y-4">
          <div className="text-center">
            Ce chantier a été vendu avant l'activation du suivi de chantier. Activez-le pour
            planifier le métré et la pose et suivre les commandes fournisseurs.
          </div>
          <div className="flex justify-center">
            {/* Le title va sur le wrapper, pas sur le Button : disabled:pointer-events-none le
                rendrait muet au survol. Voir DisabledHint. */}
            <DisabledHint hint={reasonIfDenied(PERMISSIONS.PLANNING_MANAGE)}>
              <Button
                size="sm"
                data-testid="project-planning-activate-btn"
                className="gap-2"
                onClick={handleActivate}
                disabled={isActivating || !can(PERMISSIONS.PLANNING_MANAGE)}
              >
                <CalendarCheck className="h-4 w-4" />
                {isActivating ? 'Activation…' : 'Activer le suivi de chantier'}
              </Button>
            </DisabledHint>
          </div>
        </div>
      </div>
    </section>
  )
}
